import { format } from "date-fns";
import type { AttributeLogAttribution, AttributeLogChange } from "@/api";
import { ACCESS_ATTRIBUTE } from "@/access";
import { describeAttributeValue, WHO_CAN_SET_CUTOFF } from "@/attributes";
import { MODULES } from "@/config";
import { describeModuleMove } from "@/modules";

export type LogField = "inherited_from" | "whocanset";

export type LogTarget =
    | { scope: "value" }
    | { scope: "bit"; bit: bigint }
    | { scope: "item"; item: number }
    | { scope: "field"; field: LogField };

export type LogScope = LogTarget["scope"];

export type LogStatus = "loading" | "loaded" | "error";

// What the change is read against: which attribute it belongs to, whose log it
// is, and how to name a user it points at by id. The log names the actor
// itself, but not the user an attribute is inherited from.
export interface LogContext {
    attribute?: string;
    idUser?: number;
    nameOf?: (idUser: number) => string;
}

const nameUser = (context: LogContext, idUser: number): string =>
    context.nameOf?.(idUser) ?? `ID:${idUser}`;

// A module value is three characters and most attributes are free text, so the
// mask only parses for the ones the API summarizes bit by bit.
export const maskOf = (value: string | null): bigint => {
    try {
        return BigInt(value?.split(",", 1)[0] || "0");
    } catch {
        return 0n;
    }
};

const MODULE_ATTRIBUTES = new Set<string>(MODULES);

// The access groups lead with their bitmask, which is not an id.
export const itemsOf = (attribute: string, value: string | null): number[] => {
    if (attribute !== "AGREEMENTS" && attribute !== ACCESS_ATTRIBUTE) {
        return [];
    }

    const tokens = (value ?? "").split(",");

    return (attribute === "AGREEMENTS" ? tokens : tokens.slice(1))
        .filter((token) => token.trim())
        .map(Number)
        .filter(Number.isInteger);
};

export const describeLastChange = (
    status: LogStatus | undefined,
    attribution: AttributeLogAttribution | undefined,
    scope: LogScope,
    context: LogContext = {}
): string => {
    if (status === undefined || status === "loading") {
        return "Loading…";
    }

    if (status === "error") {
        return "Could not load the change log";
    }

    // A bit or id the log never flipped is left out of the summary, while a
    // value always exists, so a missing one predates the log. A field is always
    // reported, with no modified time when it never changed.
    if (!attribution || attribution.modified === null) {
        const neverChanged = attribution
            ? scope === "field"
            : scope !== "value";

        return neverChanged
            ? "Never changed"
            : "Set before the logging started";
    }

    return `Last changed ${formatWhen(attribution.modified)}\nby ${formatWho(
        attribution,
        context
    )}`;
};

const SECONDS = 1000;
const MINUTES = 60 * SECONDS;
const HOURS = 60 * MINUTES;
const DAYS = 24 * HOURS;

const relative = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

const formatRelative = (delta: number): string => {
    if (delta < MINUTES) {
        return "less than 1 minute ago";
    } else if (delta < HOURS) {
        return relative.format(-Math.round(delta / MINUTES), "minute");
    } else if (delta < DAYS) {
        return relative.format(-Math.round(delta / HOURS), "hour");
    } else {
        return relative.format(-Math.round(delta / DAYS), "day");
    }
};

const formatWhen = (modified: number): string => {
    const delta = Date.now() - modified * 1000;

    if (delta < DAYS * 7) {
        return formatRelative(delta);
    }
    return format(new Date(modified * 1000), "yyyy-MM-dd HH:mm");
};

// Extract logs a non-positive id for the changes no user made, and the API
// leaves the actor out for those.
const formatWho = (
    attribution: AttributeLogAttribution,
    context: LogContext
): string => {
    if (attribution.changed_by) {
        return attribution.changed_by.username;
    }

    return attribution.changed_by_id_user !== null &&
        attribution.changed_by_id_user > 0
        ? nameUser(context, attribution.changed_by_id_user)
        : "unknown user";
};

const formatMove = (from: string | number | null, to: string | number | null) =>
    `${from ?? "Not set"} → ${to ?? "unset"}`;

// Worded the way the picker in AttributeWhoCanSet words it.
const formatWhoCanSet = (value: number): string => {
    if (value >= WHO_CAN_SET_CUTOFF) {
        return "Everyone";
    }

    switch (value) {
        case 0:
            return "This user";
        case 1:
            return "One level below";
        case 2:
            return "Two levels below";
    }

    return value > 0
        ? `${value} levels below`
        : `${-value} level${value === -1 ? "" : "s"} above`;
};

// Worded the way AttributeInherit words the control: no one to inherit from is
// the default, and the user's own id is the value set on them.
const formatInheritedFrom = (
    idUser: number | null,
    context: LogContext
): string => {
    if (idUser === null) {
        return "Follow default";
    }

    return idUser === context.idUser
        ? "Own value"
        : `Inherit from ${nameUser(context, idUser)}`;
};

const formatInheritedFromMove = (
    change: AttributeLogChange,
    context: LogContext
): string =>
    formatMove(
        formatInheritedFrom(change.old_inherited_from, context),
        formatInheritedFrom(change.new_inherited_from, context)
    );

export interface DescribedChange {
    when: string;
    who: string;
    what: string;
}

// The label the section would render, so the log reads the way its page does.
const describeSide = (
    value: string | null,
    context: LogContext
): string | null =>
    value === null
        ? null
        : describeAttributeValue(
              context.attribute ?? "",
              value,
              context.nameOf
          ) ?? value;

const describeValue = (
    change: AttributeLogChange,
    context: LogContext
): string[] => {
    const moved = MODULE_ATTRIBUTES.has(context.attribute ?? "")
        ? describeModuleMove(change.old_value, change.new_value)
        : null;

    if (moved) {
        return [moved];
    }

    const from = describeSide(change.old_value, context);
    const to = describeSide(change.new_value, context);

    // Two spellings of one value, such as an empty mask written as "" and as
    // "0", would otherwise read as a move that never happened.
    return from === to ? [] : [formatMove(from, to)];
};

const describeBit = (change: AttributeLogChange, bit: bigint): string[] => {
    const before = maskOf(change.old_value);
    const after = maskOf(change.new_value);

    return (before ^ after) & bit ? [(after & bit) !== 0n ? "On" : "Off"] : [];
};

const describeItem = (
    change: AttributeLogChange,
    item: number,
    context: LogContext
): string[] => {
    const attribute = context.attribute ?? "";
    const before = itemsOf(attribute, change.old_value).includes(item);
    const after = itemsOf(attribute, change.new_value).includes(item);

    return before === after ? [] : [after ? "Added" : "Removed"];
};

// Where the value comes from belongs to the attribute rather than to one bit of
// it, so every target reports it. Who may set it does not: that is a control of
// its own, with a tooltip of its own.
const describeInheritance = (
    change: AttributeLogChange,
    context: LogContext
): string[] =>
    change.old_inherited_from === change.new_inherited_from
        ? []
        : [formatInheritedFromMove(change, context)];

// Empty when the change moved nothing this target reports (ex. a re-parenting that
// only moved the inherit depth), so the caller can leave it out.
const describeWhat = (
    change: AttributeLogChange,
    target: LogTarget,
    context: LogContext
): string => {
    if (target.scope === "field") {
        if (change[`old_${target.field}`] === change[`new_${target.field}`]) {
            return "";
        }

        return target.field === "whocanset"
            ? formatMove(
                  formatWhoCanSet(change.old_whocanset),
                  formatWhoCanSet(change.new_whocanset)
              )
            : formatInheritedFromMove(change, context);
    }

    const moved =
        target.scope === "value"
            ? describeValue(change, context)
            : target.scope === "bit"
            ? describeBit(change, target.bit)
            : describeItem(change, target.item, context);

    return [...moved, ...describeInheritance(change, context)].join("\n");
};

export const describeChange = (
    change: AttributeLogChange,
    target: LogTarget,
    context: LogContext = {}
): DescribedChange => ({
    when: change.modified === null ? "–" : formatWhen(change.modified),
    who: formatWho(change, context),
    what: describeWhat(change, target, context),
});

// A change shows up wherever it has something to say.
export const describeChanges = (
    changes: AttributeLogChange[],
    target: LogTarget,
    context: LogContext = {}
): DescribedChange[] =>
    changes
        .map((change) => describeChange(change, target, context))
        .filter((described) => described.what);

export const newestChange = (
    changes: AttributeLogChange[],
    target: LogTarget,
    context: LogContext = {}
): AttributeLogChange | undefined =>
    changes.find((change) => describeWhat(change, target, context));
